import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { StreamEvent, StreamFn, StreamResult } from '@fortem-monorepo/web-sdk'

type StreamStatus = 'idle' | 'running' | 'completed' | 'failed'

export type UseReportStreamState<TResult extends StreamResult = StreamResult> = {
  run_id?: string
  status: StreamStatus
  progress: number
  events: StreamEvent<TResult>[]
  error: string | null
  result: TResult | null
}

const createDefaultState = <
  TResult extends StreamResult = StreamResult,
>(): UseReportStreamState<TResult> => ({
  status: 'idle',
  progress: 0,
  events: [],
  error: null,
  result: null,
})

type UseReportStreamOptions<
  TParams extends Record<string, unknown>,
  TResult extends StreamResult = StreamResult,
> = {
  reportType: string
  params: TParams
  streamFn: StreamFn<TParams, TResult>
}

export function useReportStream<
  TParams extends Record<string, unknown>,
  TResult extends StreamResult = StreamResult,
>({ reportType, params, streamFn }: UseReportStreamOptions<TParams, TResult>) {
  const queryClient = useQueryClient()

  const queryKey = ['reports', reportType, params] as const

  const query = useQuery({
    queryKey,
    queryFn: async () => createDefaultState<TResult>(),
    initialData: createDefaultState<TResult>(),
    staleTime: Infinity,
    gcTime: Infinity,
  })

  const mutation = useMutation({
    mutationKey: queryKey,

    mutationFn: async () => {
      queryClient.setQueryData<UseReportStreamState<TResult>>(queryKey, {
        ...createDefaultState<TResult>(),
        status: 'running',
      })

      return new Promise<TResult>((resolve, reject) => {
        const unsubscribe = streamFn({
          params,

          onEvent: (event) => {
            queryClient.setQueryData<UseReportStreamState<TResult>>(
              queryKey,
              (old = createDefaultState<TResult>()) => ({
                ...old,
                run_id: event.run_id,
                status:
                  event.event_type === 'STAGE_FAILED'
                    ? 'failed'
                    : event.event_type === 'STREAM_COMPLETED'
                      ? 'completed'
                      : 'running',
                progress: event.progress_pct ?? old.progress,
                events: [...old.events, event],
                error: event.error ?? old.error,
                result: event.result ?? old.result,
              }),
            )
          },

          onComplete: (event) => {
            unsubscribe()

            if (!event.result) {
              const error = new Error('Missing final report')

              queryClient.setQueryData<UseReportStreamState<TResult>>(
                queryKey,
                (old = createDefaultState<TResult>()) => ({
                  ...old,
                  status: 'failed',
                  error: error.message,
                }),
              )

              reject(error)
              return
            }

            queryClient.setQueryData<UseReportStreamState<TResult>>(
              queryKey,
              (old = createDefaultState<TResult>()) => ({
                ...old,
                status: 'completed',
                progress: 100,
                result: event.result!,
              }),
            )

            resolve(event.result)
          },

          onError: (error) => {
            unsubscribe()

            queryClient.setQueryData<UseReportStreamState<TResult>>(
              queryKey,
              (old = createDefaultState<TResult>()) => ({
                ...old,
                status: 'failed',
                error: error.message,
              }),
            )

            reject(error)
          },
        })
      })
    },
  })

  const state = query.data

  return {
    state,
    query,
    mutation,
    start: mutation.mutate,
    startAsync: mutation.mutateAsync,
    reset: () => {
      mutation.reset()
      queryClient.setQueryData(queryKey, createDefaultState<TResult>())
    },
    isIdle: state.status === 'idle',
    isRunning: state.status === 'running',
    isCompleted: state.status === 'completed',
    isFailed: state.status === 'failed',
  }
}
